/**
 * Profile card at the top of the participant page: who the participant is, their Google Health
 * connection (status, masked account IDs, device check) and Disconnect.
 */
import { httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import { functions } from '../../firebase';

/** Shows the last 4 characters; the full value only on request. */
function Masked({ value }) {
  const [shown, setShown] = useState(false);
  if (!value) return 'N/A';
  const masked = value.length > 4 ? `${'•'.repeat(Math.min(value.length - 4, 8))}${value.slice(-4)}` : value;
  return (
    <span>
      {shown ? value : masked}{' '}
      <button type="button" className="ja-btn ja-btn-ghost" style={{ padding: '0 6px', fontSize: 11 }}
        onClick={() => setShown(!shown)}>
        {shown ? 'hide' : 'show'}
      </button>
    </span>
  );
}
Masked.propTypes = { value: PropTypes.string };

export default function ParticipantProfile({ participantId, projectId, participant, onChanged }) {
  const { addAlert } = useAlert();
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);
  const health = participant?.health;
  const connected = health?.connected === true;

  const runCheck = async () => {
    setBusy(true);
    try {
      const res = await httpsCallable(functions, 'checkHealthConnection')({ participantId });
      setCheck(res.data);
    } catch (err) {
      addAlert('error', `Check failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Disconnect this participant? This revokes Jamasp\'s access at Google.')) return;
    setBusy(true);
    try {
      await httpsCallable(functions, 'disconnectHealth')({ participantId });
      addAlert('success', 'Disconnected');
      if (onChanged) onChanged();
    } catch (err) {
      addAlert('error', `Disconnect failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  let device = 'Press “Check connection” to read the paired device.';
  if (check && !check.ok) device = <span style={{ color: '#C0392B' }}>Not working: {check.reason || check.message}</span>;
  else if (check?.devicesError) device = `Could not be read (${check.devicesError})`;
  else if (check?.devices?.length) {
    device = check.devices.map((d) => `${d.deviceVersion || '?'} · last sync ${d.lastSyncTime || '?'}`
      + (d.batteryLevel != null ? ` · battery ${d.batteryLevel}%` : '')).join('; ');
  } else if (check) device = 'No device currently paired to this account.';

  const rows = [
    { label: 'Participant ID', value: participantId },
    { label: 'Project ID', value: projectId },
    { label: 'Prolific ID', value: participant?.pId || participant?.registration?.prolific?.pId || 'N/A' },
    { label: 'Google Health ID', value: <Masked value={health?.healthUserId} /> },
    { label: 'Fitbit ID', value: <Masked value={health?.legacyUserId} /> },
    { label: 'Connected since', value: health?.connectedAt?.toDate?.()?.toLocaleString() || 'N/A' },
    {
      label: 'Future studies',
      value: participant?.futureContact
        ? `${participant.futureContact.optIn ? 'May be contacted' : 'Declined'}`
          + (participant.futureContact.updatedAt?.toDate ? ` (${participant.futureContact.updatedAt.toDate().toLocaleDateString()})` : '')
        : 'Not asked',
    },
  ];
  if (connected) rows.push({ label: 'Device', value: device });

  return (
    <div className="ja-card" style={{ marginBottom: 20 }}>
      <div className="ja-card-header">
        <h3 className="ja-card-title">Profile</h3>
        {connected
          ? <span className="ja-chip ja-chip-active">connected</span>
          : <span className="ja-chip ja-chip-inactive">{health?.status || 'not connected'}</span>}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          {rows.map(({ label, value }) => (
            <tr key={label} style={{ borderBottom: '1px solid var(--jp-gray-100)' }}>
              <td style={{ padding: '9px 0', color: 'var(--jp-gray-600)', width: 160, fontSize: 12, fontWeight: 500 }}>{label}</td>
              <td style={{ padding: '9px 0', fontFamily: 'var(--jp-mono)', fontSize: 12 }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {connected && (
        <div className="ja-btn-row" style={{ marginTop: 16 }}>
          <button className="ja-btn ja-btn-outline" disabled={busy} onClick={runCheck}>Check connection</button>
          <button className="ja-btn ja-btn-ghost" disabled={busy} onClick={disconnect}>Disconnect</button>
        </div>
      )}
    </div>
  );
}

ParticipantProfile.propTypes = {
  participantId: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  participant: PropTypes.object,
  onChanged: PropTypes.func,
};
